import { useState, useEffect } from 'react';
import { useQueryState, parseAsInteger, parseAsStringLiteral } from 'nuqs';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import axiosInstance from '@/lib/axios';
import { triggerBlobFileDownload } from '@/lib/trigger-download';
import { formatDisplayPhone } from '@/lib/phone-utils';
import { formatBusinessCategoryLabel } from '@/lib/business-category';
import { communityHasAvenues } from '@/lib/community-platform-tags';
import { useAdminMembers } from '@/hooks/use-admin-members';
import { useUser } from '@/hooks/use-user';
import {
  Search,
  Loader2,
  Phone,
  Mail,
  MoreHorizontal,
  Shield,
  ShieldOff,
  Download,
  UserCog,
  Users,
  UserMinus,
  Signpost,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useDebounce } from 'use-debounce';
import { MemberDetailsDialog } from './MemberDetailsDialog';
import { MemberAvenuesDialog } from './MemberAvenuesDialog';
import type { AdminMember, Community, CommunityRole } from '@/types';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { SegmentedTabBar } from '@/components/ui/segmented-tabs';

const DIRECTORY_ROLE_TABS = ['ADMIN', 'LEADERSHIP', 'COMMITTEE', 'MEMBER'] as const;

const roleTabLabels: Record<(typeof DIRECTORY_ROLE_TABS)[number], string> = {
  ADMIN: 'Admin',
  LEADERSHIP: 'Leadership',
  COMMITTEE: 'Committee',
  MEMBER: 'Member',
};

const rolePriority: Record<CommunityRole, number> = {
  ADMIN: 0,
  LEADERSHIP: 1,
  COMMITTEE: 2,
  MEMBER: 3,
};

const PROTECTED_COMMUNITY_ROLES = ['CREATOR', 'OWNER'] as const;

function canRemoveFromCommunity(member: AdminMember, currentUserId?: string): boolean {
  if (currentUserId && member.id === currentUserId) return false;
  if (!member.roles.includes('MEMBER')) return false;
  if (member.roles.some((r) => (PROTECTED_COMMUNITY_ROLES as readonly string[]).includes(r))) {
    return false;
  }
  return true;
}

interface AdminMembersViewProps {
  community: Community;
}

export function AdminMembersView({ community }: AdminMembersViewProps) {
  const [page, setPage] = useQueryState(
    'page',
    parseAsInteger.withDefault(1).withOptions({ shallow: false })
  );
  const [roleTab, setRoleTab] = useQueryState(
    'role',
    parseAsStringLiteral(DIRECTORY_ROLE_TABS).withDefault('MEMBER').withOptions({ shallow: false })
  );
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebounce(search, 500);
  const { user } = useUser();
  const userCommunity = user?.communities.find((c) => c.id === community.id);
  const userRoles = userCommunity?.roles || [];
  const isAdmin = userRoles.some((role) =>
    ['ADMIN', 'CREATOR', 'OWNER'].includes(role.toUpperCase())
  );
  const { members, metadata, isLoading, mutate } = useAdminMembers(community.id, {
    search: debouncedSearch,
    page: page,
    limit: 100,
    role: roleTab,
  });
  const [selectedMember, setSelectedMember] = useState<AdminMember | null>(null);
  const [memberToRemove, setMemberToRemove] = useState<AdminMember | null>(null);
  const [isUpdating, setIsUpdating] = useState<string | null>(null);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [includeOnboarding, setIncludeOnboarding] = useState(false);
  const [memberForAvenues, setMemberForAvenues] = useState<AdminMember | null>(null);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, roleTab, setPage]);

  const toggleRole = async (member: AdminMember, role: CommunityRole) => {
    setIsUpdating(member.id);
    const hasRole = member.roles.includes(role);
    let newRoles: CommunityRole[];

    if (hasRole) {
      if (role === 'MEMBER' && member.roles.length === 1) {
        toast.error('Member must have at least the MEMBER role');
        setIsUpdating(null);
        return;
      }
      newRoles = member.roles.filter((r) => r !== role);
    } else {
      newRoles = [...member.roles, role];
    }

    try {
      await axiosInstance.patch(`/api/communities/${community.id}/members/${member.id}/roles`, {
        roles: newRoles,
      });
      toast.success(hasRole ? `${role} role removed` : `${role} role assigned`);
      mutate();
    } catch (error) {
      console.error('Failed to update role:', error);
      toast.error('Failed to update role');
    } finally {
      setIsUpdating(null);
    }
  };

  const removeFromCommunity = async (member: AdminMember) => {
    setIsUpdating(member.id);
    try {
      await axiosInstance.delete(`/api/communities/${community.id}/members/${member.id}`);
      toast.success(`${member.name} was removed from the community`);
      if (selectedMember?.id === member.id) setSelectedMember(null);
      setMemberToRemove(null);
      mutate();
    } catch (error) {
      console.error('Failed to remove member:', error);
      const message =
        axios.isAxiosError(error) &&
        error.response?.data &&
        typeof error.response.data === 'object' &&
        'error' in error.response.data
          ? String((error.response.data as { error: string }).error)
          : 'Failed to remove member';
      toast.error(message);
    } finally {
      setIsUpdating(null);
    }
  };

  const getRoleIcon = (role: CommunityRole) => {
    switch (role) {
      case 'ADMIN':
        return <Shield className="h-4 w-4" />;
      case 'LEADERSHIP':
        return <UserCog className="h-4 w-4" />;
      case 'COMMITTEE':
        return <Users className="h-4 w-4" />;
      case 'MEMBER':
        return <Users className="h-4 w-4" />;
    }
  };

  const handleDownloadCSV = async (includeOnboardingDetails: boolean = false) => {
    try {
      const response = await axiosInstance.get(
        `/api/communities/${community.id}/members/csv?onboarding=${includeOnboardingDetails}`,
        {
          responseType: 'blob',
        }
      );

      const blob = response.data as Blob;
      triggerBlobFileDownload(blob, `${community.name.replace(/\s+/g, '_')}_members.csv`);
      toast.success('CSV downloaded successfully');
    } catch (error) {
      console.error('Failed to download CSV:', error);
      toast.error('Failed to download CSV');
    } finally {
      setIsDownloadModalOpen(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold tracking-tight text-foreground">Member Directory</h1>
            {!isLoading && (
              <Badge
                variant="secondary"
                className="px-3 py-1 rounded-full font-bold text-sm shadow-sm transition-all hover:scale-105 select-none bg-primary/10 text-primary border-primary/20"
              >
                {metadata?.totalCount || 0}{' '}
                {(metadata?.totalCount || 0) === 1
                  ? roleTabLabels[roleTab].toLowerCase()
                  : `${roleTabLabels[roleTab].toLowerCase()}s`}
              </Badge>
            )}
          </div>
          <p className="text-muted-foreground">
            Browse people by role. Each row still shows every role assigned to that person.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {isAdmin && (
            <Button
              variant="outline"
              className="h-11 rounded-xl gap-2 font-semibold border-primary/20 hover:bg-primary/5 hover:text-primary transition-all shadow-sm"
              onClick={() => setIsDownloadModalOpen(true)}
              disabled={isLoading || members.length === 0}
            >
              <Download className="h-4 w-4" />
              <span>Download CSV</span>
            </Button>
          )}
          <div className="relative w-full md:w-80 group">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground group-focus-within:text-primary transition-colors pointer-events-none" />
            <Input
              placeholder="Search by name, phone, email..."
              className="bg-accent/5 pl-11 font-medium"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
      </div>

      <SegmentedTabBar
        value={roleTab}
        onValueChange={(value) => {
          void setRoleTab(value);
        }}
        variant="compact"
        className="w-full"
        items={DIRECTORY_ROLE_TABS.map((role) => ({
          value: role,
          label: roleTabLabels[role],
        }))}
      />

      <Card className="border-border/50 bg-card/50 backdrop-blur-md shadow-2xl rounded-2xl overflow-hidden">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader className="bg-accent/5">
                <TableRow className="border-border/5 hover:bg-transparent">
                  <TableHead className="w-[300px] font-bold text-xs uppercase tracking-widest py-4">
                    Member
                  </TableHead>
                  <TableHead className="font-bold text-xs uppercase tracking-widest py-4">
                    Contact
                  </TableHead>
                  <TableHead className="font-bold text-xs uppercase tracking-widest py-4">
                    Business
                  </TableHead>
                  <TableHead className="font-bold text-xs uppercase tracking-widest py-4">
                    Roles
                  </TableHead>
                  <TableHead className="text-right font-bold text-xs uppercase tracking-widest py-4">
                    Actions
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={4} className="h-64 text-center">
                      <div className="flex flex-col items-center justify-center gap-3">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                        <p className="text-sm font-medium text-muted-foreground">
                          Loading members...
                        </p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : members.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="h-64 text-center">
                      <div className="flex flex-col items-center justify-center gap-4">
                        <div className="bg-accent/5 p-4 rounded-full">
                          <Search className="h-8 w-8 text-muted-foreground/30" />
                        </div>
                        <p className="text-sm font-medium text-muted-foreground">
                          No {roleTabLabels[roleTab].toLowerCase()}s found
                          {debouncedSearch ? ' matching your search' : ''}.
                        </p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  members.map((member) => (
                    <TableRow
                      key={member.id}
                      className="group border-border/5 hover:bg-accent/5 cursor-pointer transition-colors"
                      onClick={() => setSelectedMember(member)}
                    >
                      <TableCell className="py-4">
                        <div className="flex items-center gap-4">
                          <Avatar className="h-10 w-10 border-2 border-primary/10 group-hover:border-primary/30 transition-all">
                            <AvatarImage src={member.image} className="object-cover" />
                            <AvatarFallback className="bg-gradient-premium text-white font-bold">
                              {member.name[0]}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex flex-col min-w-0">
                            <span className="font-bold text-foreground group-hover:text-primary transition-colors truncate">
                              {member.name}
                            </span>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="py-4">
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-2 text-sm text-muted-foreground group-hover:text-foreground transition-colors font-medium">
                            <Phone className="h-3.5 w-3.5 opacity-50" />
                            {formatDisplayPhone(member.phone)}
                          </div>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground group-hover:text-foreground transition-colors font-medium">
                            <Mail className="h-3.5 w-3.5 opacity-50" />
                            {member.email}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="py-4">
                        {member.businessName ? (
                          <div className="flex flex-col">
                            <span className="text-sm font-bold text-foreground">
                              {member.businessName}
                            </span>
                            <span className="text-xs text-muted-foreground font-medium">
                              {formatBusinessCategoryLabel(
                                member.businessCategory,
                                member.subCategory,
                                member.selfDefinedCategory
                              )}
                            </span>
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground italic">
                            None specified
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="py-4">
                        <div className="flex flex-wrap gap-1">
                          {[...member.roles]
                            .sort((a, b) => rolePriority[a] - rolePriority[b])
                            .map((role) => (
                              <Badge
                                key={role}
                                className="text-[10px] uppercase font-extrabold px-1.5 py-0 rounded-md"
                              >
                                {role}
                              </Badge>
                            ))}
                        </div>
                      </TableCell>
                      <TableCell className="text-right py-4">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="rounded-full bg-background border border-border/10 shadow-sm"
                              onClick={(e) => e.stopPropagation()}
                              disabled={isUpdating === member.id}
                            >
                              {isUpdating === member.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <MoreHorizontal className="h-4 w-4" />
                              )}
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent
                            align="end"
                            className="w-56 rounded-xl p-1 shadow-xl"
                          >
                            <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground uppercase">
                              Manage Roles
                            </div>
                            <DropdownMenuSeparator className="my-1" />
                            {(
                              ['ADMIN', 'LEADERSHIP', 'COMMITTEE', 'MEMBER'] as CommunityRole[]
                            ).map((role) => {
                              const hasRole = member.roles.includes(role);
                              return (
                                <DropdownMenuItem
                                  key={role}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleRole(member, role);
                                  }}
                                  className="flex items-center gap-2 py-2.5 cursor-pointer rounded-lg font-medium"
                                >
                                  {hasRole ? (
                                    <>
                                      <ShieldOff className="h-4 w-4 text-destructive" />
                                      <span>Remove from {role}</span>
                                    </>
                                  ) : (
                                    <>
                                      {getRoleIcon(role)}
                                      <span>Add as {role}</span>
                                    </>
                                  )}
                                </DropdownMenuItem>
                              );
                            })}
                            {community.isProjectsEnabled &&
                              communityHasAvenues(community.platformTags) && (
                                <>
                                  <DropdownMenuSeparator className="my-1" />
                                  <DropdownMenuItem
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setMemberForAvenues(member);
                                    }}
                                    className="flex items-center gap-2 py-2.5 cursor-pointer rounded-lg font-medium"
                                  >
                                    <Signpost className="h-4 w-4" />
                                    <span>Assign avenues</span>
                                  </DropdownMenuItem>
                                </>
                              )}
                            {canRemoveFromCommunity(member, user?.id) && (
                              <>
                                <DropdownMenuSeparator className="my-1" />
                                <DropdownMenuItem
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setMemberToRemove(member);
                                  }}
                                  className="flex items-center gap-2 py-2.5 cursor-pointer rounded-lg font-medium text-destructive focus:text-destructive"
                                >
                                  <UserMinus className="h-4 w-4" />
                                  <span>Remove from community</span>
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
        {metadata && metadata.totalPages > 1 && (
          <div className="flex items-center justify-center gap-4 py-4 border-t border-border/5">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="rounded-lg font-semibold"
            >
              Previous
            </Button>
            <span className="text-sm font-medium">
              Page {page} of {metadata.totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.min(metadata.totalPages, p + 1))}
              disabled={page === metadata.totalPages}
              className="rounded-lg font-semibold"
            >
              Next
            </Button>
          </div>
        )}
      </Card>

      <MemberDetailsDialog
        member={selectedMember}
        community={community}
        onClose={() => setSelectedMember(null)}
      />

      <MemberAvenuesDialog
        communityId={community.id}
        member={memberForAvenues}
        onClose={() => setMemberForAvenues(null)}
      />

      <AlertDialog
        open={Boolean(memberToRemove)}
        onOpenChange={(open) => {
          if (!open) setMemberToRemove(null);
        }}
      >
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove member from community?</AlertDialogTitle>
            <AlertDialogDescription>
              {memberToRemove
                ? `${memberToRemove.name} will lose access to this community. They can request to join again later if your community allows it.`
                : ''}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="rounded-xl bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={!memberToRemove || isUpdating === memberToRemove?.id}
              onClick={() => {
                if (memberToRemove) void removeFromCommunity(memberToRemove);
              }}
            >
              {memberToRemove && isUpdating === memberToRemove.id ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                'Remove member'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {isAdmin && (
        <Dialog open={isDownloadModalOpen} onOpenChange={setIsDownloadModalOpen}>
          <DialogContent className="sm:max-w-[425px] rounded-2xl">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold">Download Member Data</DialogTitle>
              <DialogDescription>
                Choose the details you want to include in your CSV export.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-6 py-6">
              <div
                className="flex items-center space-x-3 bg-accent/5 p-4 rounded-xl border border-border/50 transition-colors hover:bg-accent/10 cursor-pointer"
                onClick={() => setIncludeOnboarding((v) => !v)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setIncludeOnboarding((v) => !v);
                  }
                }}
              >
                <Checkbox
                  checked={includeOnboarding}
                  onCheckedChange={(v: boolean | 'indeterminate') =>
                    setIncludeOnboarding(v === true)
                  }
                />
                <div className="grid gap-1.5 leading-none cursor-pointer">
                  <Label className="text-sm font-bold leading-none cursor-pointer">
                    Include additional details
                  </Label>
                  <p className="text-xs text-muted-foreground font-medium">
                    Add members&apos; answers from the community&apos;s extra questions to the CSV.
                  </p>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="ghost"
                onClick={() => setIsDownloadModalOpen(false)}
                className="rounded-xl font-semibold"
              >
                Cancel
              </Button>
              <Button
                onClick={() => handleDownloadCSV(includeOnboarding)}
                className="rounded-xl font-bold px-8 bg-gradient-premium hover:opacity-90 transition-all"
              >
                <Download className="mr-2 h-4 w-4" />
                Download CSV
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
